/**
 * @file page.tsx
 * @description 서비스 이용약관 페이지
 *
 * BIDIX 서비스 이용약관을 표시하는 페이지입니다.
 * Sticky TOC 레이아웃과 강조 박스를 포함한 법적 안전성과 가독성을 고려한 구조입니다.
 *
 * 주요 기능:
 * 1. 좌측 고정 목차 (Sticky TOC)
 * 2. 실전 분석(Red Box)과 가상 시뮬레이션(Indigo Box) 조항 시각적 구분
 * 3. 인쇄 지원 (no-print 클래스)
 *
 * @dependencies
 * - next/link: 내부 링크 네비게이션
 * - lucide-react: 아이콘
 * - @/components/shared/ui/button: 버튼 컴포넌트
 */

"use client";

import { Button } from "@/components/shared/ui/button";
import { Printer, AlertTriangle, Gamepad2 } from "lucide-react";

export default function TermsPage() {
  const handlePrint = () => {
    if (typeof window !== "undefined") {
      window.print();
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950 text-zinc-900 dark:text-zinc-100">
      <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:py-12 flex flex-col lg:flex-row gap-6 sm:gap-8 lg:gap-12">
        {/* [LEFT] Sticky Navigation (목차) */}
        <aside className="hidden lg:block w-64 flex-shrink-0 no-print">
          <div className="sticky top-24">
            <h3 className="text-xs font-bold text-zinc-400 dark:text-zinc-500 uppercase tracking-wider mb-4">
              Table of Contents
            </h3>
            <nav className="space-y-1 border-l border-zinc-200 dark:border-zinc-800">
              <a
                href="#chapter1"
                className="block pl-4 py-2 text-sm text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 hover:border-l-2 hover:border-zinc-900 dark:hover:border-zinc-100 transition-all -ml-[2px]"
              >
                제1장 총칙
              </a>
              <a
                href="#chapter2"
                className="block pl-4 py-2 text-sm text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 hover:border-l-2 hover:border-zinc-900 dark:hover:border-zinc-100 transition-all -ml-[2px]"
              >
                제2장 서비스 이용
              </a>
              <a
                href="#chapter3"
                className="block pl-4 py-2 text-sm text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 hover:border-l-2 hover:border-zinc-900 dark:hover:border-zinc-100 transition-all -ml-[2px]"
              >
                제3장 회원 관리
              </a>
              <a
                href="#chapter4"
                className="block pl-4 py-2 text-sm text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 hover:border-l-2 hover:border-zinc-900 dark:hover:border-zinc-100 transition-all -ml-[2px]"
              >
                제4장 책임의 제한
              </a>
              <a
                href="#chapter5"
                className="block pl-4 py-2 text-sm text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 hover:border-l-2 hover:border-zinc-900 dark:hover:border-zinc-100 transition-all -ml-[2px]"
              >
                제5장 지식재산권
              </a>
              <a
                href="#chapter6"
                className="block pl-4 py-2 text-sm text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 hover:border-l-2 hover:border-zinc-900 dark:hover:border-zinc-100 transition-all -ml-[2px]"
              >
                제6장 개인정보 보호
              </a>
              <a
                href="#chapter7"
                className="block pl-4 py-2 text-sm text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 hover:border-l-2 hover:border-zinc-900 dark:hover:border-zinc-100 transition-all -ml-[2px]"
              >
                제7장 기타
              </a>
            </nav>
          </div>
        </aside>

        {/* [RIGHT] 약관 본문 */}
        <main className="flex-1 bg-white dark:bg-zinc-900 rounded-xl sm:rounded-2xl shadow-sm border border-zinc-200 dark:border-zinc-800 p-6 sm:p-8 md:p-12 print:shadow-none print:border-none print:rounded-none print:p-0">
          <header className="mb-8 sm:mb-12 border-b-2 border-zinc-900 dark:border-zinc-100 pb-4 sm:pb-6">
            <div className="flex flex-col sm:flex-row items-start justify-between gap-4 mb-4">
              <div className="flex-1">
                <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold mb-3 sm:mb-4 tracking-tight text-zinc-900 dark:text-zinc-100">
                  서비스 이용약관
                </h1>
                <p className="text-zinc-500 dark:text-zinc-400 text-xs sm:text-sm">
                  본 약관은 BIDIX 서비스 이용에 관한 권리, 의무 및 책임사항을
                  규정합니다.
                  <br className="hidden sm:block" />
                  시행일: 2026년 2월 1일
                </p>
              </div>
              <div className="no-print self-start sm:self-auto">
                <Button
                  onClick={handlePrint}
                  variant="ghost"
                  className="text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 text-xs sm:text-sm font-medium flex items-center gap-2 px-3 py-2 rounded hover:bg-zinc-100 dark:hover:bg-zinc-800 transition"
                >
                  <Printer className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">인쇄하기</span>
                  <span className="sm:hidden">인쇄</span>
                </Button>
              </div>
            </div>
          </header>

          <div className="space-y-8 sm:space-y-12 text-xs sm:text-sm leading-relaxed text-zinc-700 dark:text-zinc-300">
            {/* 제1장 총칙 */}
            <section id="chapter1" className="scroll-mt-24">
              <h2 className="text-base sm:text-lg font-bold text-zinc-900 dark:text-zinc-100 mb-4 sm:mb-6 flex items-center gap-2">
                <span className="w-1.5 h-5 bg-zinc-900 dark:bg-zinc-100 rounded-full flex-shrink-0"></span>{" "}
                제1장 총칙
              </h2>

              <div className="space-y-4 sm:space-y-6">
                <article>
                  <h3 className="font-bold text-zinc-800 dark:text-zinc-200 mb-2 text-sm sm:text-base">제1조 (목적)</h3>
                  <p className="text-zinc-700 dark:text-zinc-300">
                    본 약관은 BIDIX(이하 &quot;회사&quot;)가 제공하는 경매 관련
                    정보 정리, 체크리스트 제공 및 비용·수익 계산 서비스(이하
                    &quot;서비스&quot;)의 이용 조건, 절차, 회사와 이용자 간의
                    권리·의무 및 책임 사항을 규정함을 목적으로 합니다.
                  </p>
                </article>

                {/* [강조] 서비스 성격 - Red Box */}
                <article className="bg-red-50 dark:bg-red-950/30 p-4 sm:p-5 rounded-xl border border-red-100 dark:border-red-900/50">
                  <h3 className="font-bold text-red-800 dark:text-red-200 mb-2 sm:mb-3 flex items-center gap-2 text-sm sm:text-base">
                    <AlertTriangle className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    제2조 (서비스의 성격)
                  </h3>
                  <ol className="list-decimal list-inside space-y-2 text-red-700/90 dark:text-red-300/90 text-xs sm:text-sm">
                    <li>
                      본 서비스는{" "}
                      <strong className="text-red-900 dark:text-red-100">판단, 추천, 자문, 중개, 투자 유도</strong>를
                      목적으로 하지 않습니다.
                    </li>
                    <li>
                      서비스는 공시된 자료 및 이용자 입력값을 기반으로{" "}
                      <strong className="text-red-900 dark:text-red-100">정리·표시·계산 결과</strong>를 제공하는
                      도구입니다.
                    </li>
                    <li>
                      서비스에서 제공되는 모든 결과는{" "}
                      <strong className="text-red-900 dark:text-red-100">확정적 사실이나 결과를 보장하지 않으며</strong>,
                      실제 결과와 차이가 발생할 수 있습니다.
                    </li>
                  </ol>
                </article>

                <article>
                  <h3 className="font-bold text-zinc-800 dark:text-zinc-200 mb-2 text-sm sm:text-base">제3조 (정의)</h3>
                  <ol className="list-decimal list-inside space-y-1 text-zinc-600 dark:text-zinc-400 pl-2 text-xs sm:text-sm">
                    <li>
                      &quot;서비스&quot;란 회사가 제공하는 웹·모바일 기반 정보
                      정리, 체크리스트, 계산 시뮬레이션 기능 일체를 의미합니다.
                    </li>
                    <li>
                      &quot;이용자&quot;란 본 약관에 따라 서비스를 이용하는 회원
                      및 비회원을 의미합니다.
                    </li>
                    <li>
                      &quot;회원&quot;이란 회사에 개인정보를 제공하고 계정을
                      생성하여 서비스를 이용하는 자를 의미합니다.
                    </li>
                    <li>
                      &quot;콘텐츠&quot;란 서비스 내에서 제공되는 사건 정보,
                      계산 결과, 체크 항목, 저장된 기록 등 일체의 자료를
                      의미합니다.
                    </li>
                    <li>
                      &quot;실제 사건 데이터&quot;란 법원 등 공공기관에 공시된
                      정보를 기술적으로 수집·정리하여 표시한 자료를 의미합니다.
                    </li>
                    {/* [추가] 가상 시뮬레이션 정의 */}
                    <li>
                      &quot;가상 시뮬레이션&quot;이란 경매 학습 및 연습을 위해
                      회사가 알고리즘으로 생성한{" "}
                      <strong className="text-zinc-900 dark:text-zinc-100">허구의 사건 데이터</strong>를 의미합니다.
                    </li>
                  </ol>
                </article>
              </div>
            </section>

            <hr className="border-zinc-100 dark:border-zinc-800" />

            {/* 제2장 서비스 이용 */}
            <section id="chapter2" className="scroll-mt-24">
              <h2 className="text-base sm:text-lg font-bold text-zinc-900 dark:text-zinc-100 mb-4 sm:mb-6 flex items-center gap-2">
                <span className="w-1.5 h-5 bg-zinc-900 dark:bg-zinc-100 rounded-full flex-shrink-0"></span>{" "}
                제2장 서비스 이용
              </h2>

              <div className="space-y-4 sm:space-y-6">
                <article>
                  <h3 className="font-bold text-zinc-800 dark:text-zinc-200 mb-2 text-sm sm:text-base">
                    제4조 (서비스 제공 내용)
                  </h3>
                  <p className="mb-2 text-zinc-700 dark:text-zinc-300">
                    회사는 다음 각 호의 서비스를 제공합니다.
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-zinc-600 dark:text-zinc-400 pl-2 text-xs sm:text-sm">
                    <li>법원 경매 사건 정보의 정리 및 표시</li>
                    <li>권리 및 확인 사항에 대한 체크리스트 제공</li>
                    <li>이용자 입력값 기반 비용·수익 계산 시뮬레이션</li>
                    <li>
                      <strong className="text-zinc-900 dark:text-zinc-100">
                        가상 데이터를 활용한 모의 투자 연습 (가상 시뮬레이션)
                      </strong>
                    </li>
                    <li>계산 조건 및 결과의 저장·열람 기능</li>
                  </ul>
                </article>

                <article>
                  <h3 className="font-bold text-zinc-800 dark:text-zinc-200 mb-2 text-sm sm:text-base">
                    제5조 (서비스 이용의 한계)
                  </h3>
                  <ol className="list-decimal list-inside space-y-1 text-zinc-600 dark:text-zinc-400 pl-2 text-xs sm:text-sm">
                    <li>
                      회사는 서비스 결과의 정확성, 완전성, 최신성을 보증하지
                      않습니다.
                    </li>
                    <li>
                      이용자는 서비스에서 제공되는 정보를{" "}
                      <strong className="text-zinc-900 dark:text-zinc-100">참고 자료</strong>로만 활용해야 하며, 실제 행위에
                      앞서 별도의 확인 절차를 거쳐야 합니다.
                    </li>
                  </ol>
                </article>

                {/* [추가] 가상 시뮬레이션 특칙 - Indigo Box */}
                <article className="bg-indigo-50 dark:bg-indigo-950/30 p-4 sm:p-5 rounded-xl border border-indigo-100 dark:border-indigo-900/50">
                  <h3 className="font-bold text-indigo-900 dark:text-indigo-200 mb-2 sm:mb-3 flex items-center gap-2 text-sm sm:text-base">
                    <Gamepad2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    제5조의2 (가상 시뮬레이션 특칙)
                  </h3>
                  <ol className="list-decimal list-inside space-y-2 text-indigo-800/90 dark:text-indigo-300/90 text-xs sm:text-sm">
                    <li>
                      &apos;가상 시뮬레이션&apos; 메뉴에서 제공되는 모든
                      정보(등기부, 감정가, 주소 등)는 학습을 위해 생성된{" "}
                      <strong className="text-indigo-900 dark:text-indigo-100">허구의 데이터(Fiction)</strong>이며, 실제
                      권리관계와 무관합니다.
                    </li>
                    <li>
                      가상 시뮬레이션 결과로 제공되는 &apos;분석 리포트&apos; 및
                      &apos;점수(Score)&apos;는 회사의 자체 알고리즘에 의한{" "}
                      <strong className="text-indigo-900 dark:text-indigo-100">학습 성취도 평가</strong>일 뿐, 실제 투자의
                      수익성을 보장하거나 예측하는 지표가 아닙니다.
                    </li>
                  </ol>
                </article>

                <article>
                  <h3 className="font-bold text-zinc-800 dark:text-zinc-200 mb-2 text-sm sm:text-base">
                    제6조 (서비스 변경 및 중단)
                  </h3>
                  <p className="text-zinc-600 dark:text-zinc-400">
                    회사는 운영상·기술상 필요에 따라 서비스의 전부 또는 일부를
                    변경하거나 중단할 수 있습니다.
                  </p>
                </article>
              </div>
            </section>

            <hr className="border-zinc-100 dark:border-zinc-800" />

            {/* 제3장 회원 관리 */}
            <section id="chapter3" className="scroll-mt-24">
              <h2 className="text-base sm:text-lg font-bold text-zinc-900 dark:text-zinc-100 mb-4 sm:mb-6 flex items-center gap-2">
                <span className="w-1.5 h-5 bg-zinc-900 dark:bg-zinc-100 rounded-full flex-shrink-0"></span>{" "}
                제3장 회원 관리
              </h2>
              <div className="space-y-4 sm:space-y-6">
                <article>
                  <h3 className="font-bold text-zinc-800 dark:text-zinc-200 mb-2 text-sm sm:text-base">
                    제7조 (회원가입)
                  </h3>
                  <p className="text-zinc-600 dark:text-zinc-400">
                    이용자는 회사가 정한 절차에 따라 회원가입을 신청할 수
                    있으며, 회사는 허위 정보 제공 등의 경우 가입을 제한할 수
                    있습니다.
                  </p>
                </article>
                <article>
                  <h3 className="font-bold text-zinc-800 dark:text-zinc-200 mb-2 text-sm sm:text-base">
                    제8조 (회원 정보 관리)
                  </h3>
                  <p className="text-zinc-600 dark:text-zinc-400">
                    회원은 자신의 정보를 열람 및 수정할 수 있으며, 정보 변경
                    미반영으로 인한 불이익은 회원 본인에게 있습니다.
                  </p>
                </article>
              </div>
            </section>

            <hr className="border-zinc-100 dark:border-zinc-800" />

            {/* 제4장 책임의 제한 */}
            <section id="chapter4" className="scroll-mt-24">
              <h2 className="text-base sm:text-lg font-bold text-zinc-900 dark:text-zinc-100 mb-4 sm:mb-6 flex items-center gap-2">
                <span className="w-1.5 h-5 bg-zinc-900 dark:bg-zinc-100 rounded-full flex-shrink-0"></span>{" "}
                제4장 책임의 제한
              </h2>
              <div className="space-y-4 sm:space-y-6">
                {/* [강조] 책임 제한 - Zinc Box */}
                <article className="bg-zinc-100 dark:bg-zinc-800/50 p-4 sm:p-5 rounded-xl border border-zinc-200 dark:border-zinc-800">
                  <h3 className="font-bold text-zinc-900 dark:text-zinc-100 mb-2 sm:mb-3 text-sm sm:text-base">
                    제9조 (책임 제한)
                  </h3>
                  <ol className="list-decimal list-inside space-y-2 text-zinc-700 dark:text-zinc-300 text-xs sm:text-sm">
                    <li>
                      회사는 서비스가 제공하는 정보 및 계산 결과에 대해{" "}
                      <strong className="text-zinc-900 dark:text-zinc-100">
                        어떠한 법적·재무적 책임도 부담하지 않습니다
                      </strong>
                      .
                    </li>
                    <li>
                      서비스는 감정가, 최저가, 수익, 비용, 세금 등에 대해{" "}
                      <strong className="text-zinc-900 dark:text-zinc-100">확정적 결과를 제시하지 않습니다</strong>.
                    </li>
                    <li>
                      이용자의 의사결정, 계약 체결, 입찰, 매매 등 모든 행위의
                      책임은 전적으로 이용자에게 귀속됩니다.
                    </li>
                  </ol>
                </article>

                <article>
                  <h3 className="font-bold text-zinc-800 dark:text-zinc-200 mb-2 text-sm sm:text-base">
                    제10조 (외부 정보에 대한 책임)
                  </h3>
                  <p className="text-zinc-600 dark:text-zinc-400">
                    회사는 외부 기관이 제공한 공시 정보의 오류, 지연, 변경으로
                    인한 손해에 대해 책임을 지지 않습니다.
                  </p>
                </article>
              </div>
            </section>

            <hr className="border-zinc-100 dark:border-zinc-800" />

            {/* 제5,6,7장 */}
            <section id="chapter5" className="scroll-mt-24">
              <h2 className="text-base sm:text-lg font-bold text-zinc-900 dark:text-zinc-100 mb-4 sm:mb-6">
                제5장 지식재산권
              </h2>
              <p className="text-zinc-600 dark:text-zinc-400">
                서비스 및 콘텐츠에 대한 저작권은 회사에 귀속되며, 회사의 사전
                동의 없는 상업적 이용을 금합니다.
              </p>
            </section>

            <section id="chapter6" className="scroll-mt-24 mt-8 sm:mt-12">
              <h2 className="text-base sm:text-lg font-bold text-zinc-900 dark:text-zinc-100 mb-4 sm:mb-6">
                제6장 개인정보 보호
              </h2>
              <p className="text-zinc-600 dark:text-zinc-400">
                회사는 관련 법령에 따라 이용자의 개인정보를 보호하며, 자세한
                사항은 개인정보처리방침에 따릅니다.
              </p>
            </section>

            <section id="chapter7" className="scroll-mt-24 mt-8 sm:mt-12">
              <h2 className="text-base sm:text-lg font-bold text-zinc-900 dark:text-zinc-100 mb-4 sm:mb-6">
                제7장 기타
              </h2>
              <div className="space-y-3 sm:space-y-4">
                <p className="text-zinc-600 dark:text-zinc-400">
                  <strong className="text-zinc-800 dark:text-zinc-200">
                    제13조 (약관의 개정)
                  </strong>{" "}
                  회사는 법령을 위반하지 않는 범위에서 약관을 개정할 수 있으며,
                  사전 공지합니다.
                </p>
                <p className="text-zinc-600 dark:text-zinc-400">
                  <strong className="text-zinc-800 dark:text-zinc-200">
                    제14조 (준거법 및 관할)
                  </strong>{" "}
                  본 약관은 대한민국 법령을 따르며, 분쟁 시 민사소송법상 관할
                  법원을 따릅니다.
                </p>
              </div>
            </section>

            <hr className="border-zinc-200 dark:border-zinc-800" />

            <section className="scroll-mt-24">
              <h3 className="font-bold text-zinc-900 dark:text-zinc-100 mb-2 text-sm sm:text-base">부칙</h3>
              <p className="text-zinc-600 dark:text-zinc-400">
                본 약관은 2026년 2월 1일부터 시행합니다.
              </p>
            </section>
          </div>
        </main>
      </div>
    </div>
  );
}
